'use client'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useLocales } from '@context/LocaleProvider'
import { looksLikeLocale, localePath, isSiteLocale, FALLBACK_LOCALES } from '@utils/locales'

// Kept for older imports. Static fallback only — use useLocales() in new code.
export const LOCALES = FALLBACK_LOCALES.locales
export const DEFAULT_LOCALE = FALLBACK_LOCALES.defaultLocale

const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i                 // https:, mailto:, tel:, whatsapp:, line:
const BARE_DOMAIN_RE = /^[^/?#\s]+\.[a-z]{2,}(\/|\?|$)/i  // wa.me/123, www.site.com

function classifyHref(raw) {
  const h = String(raw ?? '').trim()
  if (!h) return { external: false, url: '/' }
  if (SCHEME_RE.test(h) || h.startsWith('//')) return { external: true, url: h }
  if (BARE_DOMAIN_RE.test(h)) return { external: true, url: `https://${h}` }
  return { external: false, url: h }
}

const LinkComponent = ({ href, className, style, children, target, rel, ...rest }) => {
  const pathname = usePathname() ?? '/'
  const site = useLocales()
  const { locales, defaultLocale } = site
  const { external, url } = classifyHref(href)
  const safeRel = target === '_blank' ? (rel ?? 'noopener noreferrer') : rel

  // External links (WhatsApp, LINE, mailto, tel, other sites): plain <a>, never prefixed
  if (external) {
    return (
      <a href={url} className={className} style={style} target={target} rel={safeRel} {...rest}>
        {children}
      </a>
    )
  }

  // Hash / query-only links stay on the current page
  if (url.startsWith('#') || url.startsWith('?')) {
    return <Link href={url} className={className} style={style} target={target} rel={safeRel} {...rest}>{children}</Link>
  }

  let link = url.startsWith('/') ? url : `/${url}`

  // Current language from the URL, checked against the site's enabled languages
  const currentSeg = pathname.split('/')[1]
  const currentLocale = isSiteLocale(currentSeg, site) ? currentSeg : null

  // Already has a language prefix (enabled or not) → leave it alone, never double-prefix.
  // Safe because CMS slugs and app routes can't be locale-shaped.
  const hrefSeg = link.split('/')[1]
  const hrefHasLocale = locales.includes(hrefSeg) || looksLikeLocale(hrefSeg)

  // Only prefix NON-default locales
  if (currentLocale && currentLocale !== defaultLocale && !hrefHasLocale) {
    link = localePath(currentLocale, link, defaultLocale)
  }

  return (
    <Link href={link} className={className} style={style} target={target} rel={safeRel} {...rest}>
      {children}
    </Link>
  )
}

export default LinkComponent